import KeyboardDoubleArrowUpIcon from "@mui/icons-material/KeyboardDoubleArrowUp";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { Box, Button, LinearProgress, Typography } from "@mui/material";
import React from "react";
import { useAppSelector } from "../../redux/hooks/redux";
import { EBenefitTypes } from "../../permissions/benefits/enums/benefits.enums";
import { selectEntitlement } from "../../permissions/benefits/redux/benefits.selectors";
import { colors } from "../ui/colors";

interface SeatLimitProgressProps {
    sx?: any;
    maxWidth?: number | string;
    hasSubscription?: boolean;
}

export const SeatLimitProgress: React.FC<SeatLimitProgressProps> = ({ sx, maxWidth = 200, hasSubscription }) => {
    const numberOfSeats = useAppSelector(selectEntitlement(EBenefitTypes.SEATS));

    const SEAT_LIMIT = numberOfSeats?.limit || 0;
    const USED_SEATS = numberOfSeats?.used || 0;

    const isAtLimit = USED_SEATS >= SEAT_LIMIT;
    const pct = SEAT_LIMIT > 0 ? Math.min((USED_SEATS / SEAT_LIMIT) * 100, 100) : 0;

    const handleUpgrade = () => {
        window.location.href = "/business/plan-center";
    };

    if (!hasSubscription) {
        return null;
    }

    return (
        <Box
            sx={{
                px: 2,
                py: 1.25,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                bgcolor: "transparent",
                ...sx
            }}
        >
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.6 }}>
                    {isAtLimit && (
                        <WarningAmberRoundedIcon sx={{ fontSize: 13, color: "#ef4444" }} />
                    )}
                    <Typography
                        sx={{
                            fontSize: "0.65rem",
                            fontWeight: 800,
                            textTransform: "uppercase",
                            letterSpacing: "0.06em",
                            color: isAtLimit ? "#dc2626" : colors.gray[500],
                        }}
                    >
                        {isAtLimit
                            ? `Limit reached (${USED_SEATS}/${SEAT_LIMIT})`
                            : `Seats used (${USED_SEATS}/${SEAT_LIMIT})`}
                    </Typography>
                </Box>
                <LinearProgress
                    variant="determinate"
                    value={pct}
                    sx={{
                        height: 6,
                        borderRadius: 3,
                        maxWidth: maxWidth,
                        bgcolor: isAtLimit ? "var(--fx-colors-danger-100, #FEE2E2)" : colors.gray[200],
                        "& .MuiLinearProgress-bar": {
                            borderRadius: 3,
                            bgcolor: isAtLimit ? "#ef4444" : colors.orange.base,
                            boxShadow: isAtLimit ? "0 0 8px rgba(239,68,68,0.4)" : "none",
                        },
                    }}
                />
            </Box>

            <Button
                disabled={isAtLimit}
                variant={isAtLimit ? "contained" : "outlined"}
                size="small"
                startIcon={<KeyboardDoubleArrowUpIcon sx={{ fontSize: "15px !important" }} />}
                onClick={handleUpgrade}
                sx={{
                    flexShrink: 0,
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: "0.78rem",
                    borderRadius: "8px",
                    px: 2,
                    py: 0.6,
                    ...(isAtLimit
                        ? {
                            bgcolor: colors.orange.base,
                            color: "#fff",
                            border: "none",
                            boxShadow: `0 4px 12px ${colors.orange.base}45`,
                            "&:hover": {
                                bgcolor: colors.orange[700] || colors.orange.base,
                                boxShadow: `0 6px 16px ${colors.orange.base}65`,
                            },
                        }
                        : {
                            borderColor: colors.orange[300],
                            color: colors.orange[700] || colors.orange.base,
                            "&:hover": {
                                bgcolor: colors.orange[50],
                                borderColor: colors.orange.base,
                            },
                        }),
                }}
            >
                Upgrade to add seats
            </Button>
        </Box>
    );
};
